import { useState } from "react"

import { Datasets } from "../../../../../constants/enums"
import { parseErrorAsObj } from "../../../../../errors/errorProcessors"
import { IAppError } from "../../../../../errors/errorTypes"
import { useV2gCommentRouting } from "../../../../../hooks/useV2gCommentRouting"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { patchClientProtected } from "../../../clients/clients"
import {
    LCT_DEVICE_READ_COMMENTS,
    TTR_DEVICE_READ_COMMENTS,
    V2G_DEVICE_READ_LCT_COMMENTS,
} from "../../../urls/private"

interface IReadDeviceCommentHook {
    isSending: boolean
    error: IAppError | undefined
    toggleDeviceComment: (
        deviceSysRef: string,
        read: boolean,
        dataset?: Datasets,
        resetSwitchOnFail?: () => void
    ) => Promise<void>
}

/**
 * Provides functions and logic for marking the comment thread on a TTR/LCT
 * device as read/unread. Pass `isV2gDevice` when the device is a V2G EVCP so
 * that V2G assessors are routed to the `/v2g/v1/` endpoints.
 */
const useReadDeviceComment = (isV2gDevice = false): IReadDeviceCommentHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const useAsV2gAssessor = useV2gCommentRouting(isV2gDevice)

    const toggleDeviceComment = async (
        deviceSysRef: string,
        read: boolean,
        dataset?: Datasets,
        resetSwitchOnFail?: () => void
    ): Promise<void> => {
        try {
            if (!dataset) {
                return
            }
            let url
            if (dataset === Datasets.TTR) {
                url = TTR_DEVICE_READ_COMMENTS(deviceSysRef, dataset)
            } else if (useAsV2gAssessor) {
                url = V2G_DEVICE_READ_LCT_COMMENTS(deviceSysRef, dataset)
            } else {
                url = LCT_DEVICE_READ_COMMENTS(deviceSysRef, dataset)
            }
            setIsSending(true)

            await patchClientProtected(url, { read })
            snackbar(
                `Comment read status updated to ${read ? "read" : "unread"}.`,
                "success"
            )
        } catch (err) {
            if (resetSwitchOnFail) {
                resetSwitchOnFail()
            }
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
            snackbar(
                "Failed to update comment read status. Please try again.",
                "warning"
            )
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        toggleDeviceComment,
    }
}

export default useReadDeviceComment
